import React, { useEffect, useRef } from 'react'
import { onboardingSteps, tourLabels } from './tourContent'

interface Props {
  language: 'en' | 'fa'
  stepIndex: number
  hasBack: boolean
  onNext: () => void
  onBack: () => void
  onSkip: () => void
}

// Selectors for elements to highlight at each pre-step (null = no highlight)
const HIGHLIGHT_SELECTOR: Record<number, string | null> = {
  0: null,
  1: '[data-tour="sidebar-workflow"]',
  2: '[data-tour="workflow-library-btn"]',
  3: null,
}

export function WelcomeModal({ language, stepIndex, hasBack, onNext, onBack, onSkip }: Props) {
  const dir = language === 'fa' ? 'rtl' : 'ltr'
  const text = onboardingSteps[stepIndex]?.[language] ?? ''
  const next = tourLabels.next[language]
  const back = tourLabels.back[language]
  const skip = tourLabels.skip[language]
  const start = language === 'fa' ? 'بله، شروع کن' : 'Yes, show me'

  const highlightRef = useRef<HTMLElement | null>(null)
  const sidebarWasMinimized = useRef<boolean>(false)

  useEffect(() => {
    // Remove any previous highlight
    if (highlightRef.current) {
      highlightRef.current.style.removeProperty('position')
      highlightRef.current.style.removeProperty('z-index')
      highlightRef.current.style.removeProperty('box-shadow')
      highlightRef.current.style.removeProperty('border-radius')
      highlightRef.current = null
    }

    // Step 1: expand sidebar and remember prior state
    if (stepIndex === 1) {
      sidebarWasMinimized.current =
        document.body.getAttribute('data-kt-app-sidebar-minimize') === 'on'
      document.body.removeAttribute('data-kt-app-sidebar-minimize')
    }

    // Highlight a target element if this step has one
    const selector = HIGHLIGHT_SELECTOR[stepIndex]
    if (selector) {
      const deadline = Date.now() + 3000
      const tryHighlight = () => {
        const el = document.querySelector<HTMLElement>(selector)
        if (!el) {
          if (Date.now() < deadline) setTimeout(tryHighlight, 100)
          return
        }
        if (!el.style.position || el.style.position === 'static') el.style.position = 'relative'
        el.style.zIndex = '10000'
        el.style.boxShadow = '0 0 0 4px var(--bs-primary, #3e97ff), 0 0 0 8px rgba(62,151,255,0.25)'
        el.style.borderRadius = '6px'
        highlightRef.current = el
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      tryHighlight()
    }

    return () => {
      // Remove highlight
      if (highlightRef.current) {
        highlightRef.current.style.removeProperty('position')
        highlightRef.current.style.removeProperty('z-index')
        highlightRef.current.style.removeProperty('box-shadow')
        highlightRef.current.style.removeProperty('border-radius')
        highlightRef.current = null
      }
      // Restore sidebar state when leaving step 1
      if (stepIndex === 1) {
        if (sidebarWasMinimized.current) {
          document.body.setAttribute('data-kt-app-sidebar-minimize', 'on')
        } else {
          document.body.removeAttribute('data-kt-app-sidebar-minimize')
        }
      }
    }
  }, [stepIndex])

  // Anchor popover near highlighted element when there is one
  const selector = HIGHLIGHT_SELECTOR[stepIndex]
  const anchorEl = selector ? document.querySelector<HTMLElement>(selector) : null
  const anchorRect = anchorEl?.getBoundingClientRect()

  let popoverStyle: React.CSSProperties = {
    position: 'fixed',
    zIndex: 10001,
    background: 'var(--bs-body-bg, #fff)',
    color: 'var(--bs-body-color, #333)',
    borderRadius: 10,
    boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
    padding: '20px 20px 14px',
    width: 300,
    maxWidth: 'calc(100vw - 32px)',
  }

  if (anchorRect) {
    // Place popover to the right of the element (or left in RTL)
    const gap = 12
    if (dir === 'rtl') {
      // Sidebar is on the right in RTL; place popover to the left of it
      popoverStyle.right = window.innerWidth - anchorRect.left + gap
      popoverStyle.top = Math.max(16, anchorRect.top + anchorRect.height / 2 - 80)
    } else {
      popoverStyle.left = anchorRect.right + gap
      popoverStyle.top = Math.max(16, anchorRect.top + anchorRect.height / 2 - 80)
    }
  } else {
    // Centered
    popoverStyle.top = '50%'
    popoverStyle.left = '50%'
    popoverStyle.transform = 'translate(-50%, -50%)'
  }

  const isWelcomeStep = stepIndex === 0

  return (
    <>
      {/* Backdrop */}
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9998 }} />

      {/* Popover */}
      <div dir={dir} style={popoverStyle}>
        {/* Close button */}
        <button
          onClick={onSkip}
          style={{
            position: 'absolute',
            top: 8,
            [dir === 'rtl' ? 'left' : 'right']: 10,
            background: 'none',
            border: 'none',
            fontSize: 18,
            cursor: 'pointer',
            opacity: 0.5,
            lineHeight: 1,
          }}
        >
          ×
        </button>

        <p style={{
          margin: '8px 0 16px',
          fontSize: '0.9rem',
          lineHeight: 1.6,
          textAlign: dir === 'rtl' ? 'right' : 'left',
        }}>
          {text}
        </p>

        <div style={{
          display: 'flex',
          gap: 8,
          justifyContent: dir === 'rtl' ? 'flex-start' : 'flex-end',
          flexWrap: 'wrap',
        }}>
          <button onClick={onSkip} className='btn btn-sm btn-link text-muted'>
            {skip}
          </button>
          {hasBack && (
            <button onClick={onBack} className='btn btn-sm btn-light'>
              {back}
            </button>
          )}
          <button onClick={onNext} className='btn btn-sm btn-primary'>
            {isWelcomeStep ? start : next}
          </button>
        </div>
      </div>
    </>
  )
}
